import { useMemo, useState } from 'react';
import { useSettingsState } from '../../context/settings';
import { classifyAddress, addressTypeLabel, AddressSelectionMode, defaultNetworkScope } from '@layerswap/utils';
const validKeys = (keys, options) => {
    const optionKeys = options.map(o => o.key);
    return (keys ?? []).filter(key => optionKeys.includes(key));
};
export function useNetworkScope(address, initial, availableNetworks) {
    const { networks } = useSettingsState();
    const [picked, setPicked] = useState(null);
    const trimmed = address.trim();
    const detected = useMemo(() => classifyAddress(trimmed), [trimmed]);
    const signature = useMemo(() => `${detected.selection}:${detected.types.map(String).sort().join(',')}`, [detected.selection, detected.types]);
    const mode = useMemo(() => {
        switch (detected.selection) {
            case AddressSelectionMode.Networks: {
                const type = detected.types[0];
                const scopedNetworks = networks.filter(n => detected.types.includes(n.type) && (!availableNetworks || availableNetworks.includes(n.name)));
                const options = scopedNetworks.map(n => ({ key: n.name, label: n.display_name, logo: n.logo }));
                return {
                    selectorProps: { sectionLabel: 'Networks', masterLabel: 'All networks', overlapping: false, options },
                    defaults: defaultNetworkScope(type, scopedNetworks),
                    saved: initial?.networks,
                    toEntry: keys => ({ networkTypes: detected.types, networks: keys }),
                };
            }
            case AddressSelectionMode.Overlap: {
                const options = detected.types.map(t => ({ key: t, label: addressTypeLabel(t), logo: networks.find(n => n.type === t)?.logo }));
                return {
                    // Single-select disambiguation: the address is exactly one of these,
                    // we just can't tell from the string — so no "both" default.
                    selectorProps: { sectionLabel: 'Network', masterLabel: '', overlapping: true, options },
                    defaults: [],
                    saved: initial?.networkTypes,
                    toEntry: keys => ({ networkTypes: keys }),
                };
            }
            default:
                return null;
        }
    }, [detected, networks, availableNetworks, initial?.networks, initial?.networkTypes]);
    const options = mode?.selectorProps.options ?? [];
    const saved = validKeys(mode?.saved, options);
    const fallback = saved.length ? saved : (mode?.defaults ?? []);
    const selected = validKeys(picked?.signature === signature ? picked.keys : fallback, options);
    const entry = mode ? mode.toEntry(selected) : { networkTypes: detected.types.length ? detected.types : undefined };
    const selector = mode && options.length > 1
        ? { ...mode.selectorProps, selected, onChange: (keys) => setPicked({ signature, keys }) }
        : null;
    return { selector, entry, provider: entry.networkTypes?.[0] };
}
